import { useCallback, useEffect, useRef, useState } from "react";
import getFocusableElements from "../utils/getFocusableElements";

const useFocusTrap = (isActive, outsideRefs = []) => {
	const [isFirstElementFocused, setIsFirstElementFocused] = useState(false);
	const trapRef = useRef(null);
	const triggerRef = useRef(null);

	const focusableSelectors = getFocusableElements();

	const getFocusableFromElement = element => {
		if (element instanceof Element) {
			return element.matches(focusableSelectors)
				? [element]
				: Array.from(element.querySelectorAll(focusableSelectors));
		}
		return [];
	};

	const updateFocusableElements = useCallback(() => {
		if (!trapRef.current) return [];

		const internalElements = Array.from(trapRef.current.querySelectorAll(focusableSelectors));
		const externalElements = outsideRefs.flatMap(ref =>
			typeof ref === "string"
				? Array.from(document.querySelectorAll(ref)).flatMap(element =>
						getFocusableFromElement(element)
				  )
				: getFocusableFromElement(ref)
		);

		return [...internalElements, ...externalElements]
			.filter(element => {
				const rect = element.getBoundingClientRect();

				return rect.width > 0 || rect.height > 0;
			})
			.sort((elementA, elementB) =>
				elementA.compareDocumentPosition(elementB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [outsideRefs]);

	useEffect(() => {
		if (!isActive) return;

		triggerRef.current = document.activeElement;

		return () => {
			triggerRef.current = null;
			setIsFirstElementFocused(false);
		};
	}, [isActive]);

	useEffect(() => {
		if (!isActive || !trapRef.current) return;

		const firstInternalElement = trapRef.current.querySelector(focusableSelectors);

		if (firstInternalElement && !isFirstElementFocused) {
			firstInternalElement.focus({ focusVisible: true });
			setIsFirstElementFocused(true);
		}

		const handleKeyDown = event => {
			if (event.key !== "Tab") return;

			event.preventDefault();

			const focusableElements = updateFocusableElements();
			const firstElement = focusableElements?.[0];
			const lastElement = focusableElements?.[focusableElements.length - 1];
			const currentIndex = focusableElements.indexOf(event.target);

			if (event.shiftKey && event.target === firstElement) {
				lastElement?.focus({ focusVisible: true });
			} else if (!event.shiftKey && event.target === lastElement) {
				firstElement?.focus({ focusVisible: true });
			} else {
				const nextElement = focusableElements[currentIndex + (event.shiftKey ? -1 : 1)];

				if (nextElement) {
					nextElement.focus({ focusVisible: true });
				}
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActive, isFirstElementFocused]);

	const focusTrigger = () => {
		triggerRef.current?.focus({ focusVisible: true });
	};

	return { focusTrigger, trapRef };
};

export default useFocusTrap;
